import PropTypes from "prop-types";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getConsentsByTenant,
  updateConsentPreferences,
  withdrawAllConsent,
} from "../services/consents";
import { getPurposes } from "../services/purposes";
import { getConsentLedger } from "../services/consentLedger";
import { formatDate, formatTimelineDate } from "../utils/formatDate";
import { Accordion } from "../components/utils/Accordion";
import { Tabs } from "../components/utils/Tabs";
import { Badge } from "../components/utils/Badge";
import { Button } from "../components/utils/Button";
import { Typography } from "../components/utils/Typography";
import { Modal } from "../components/utils/Modal";

const CONSENT_STATUS_LABELS = {
  granted: "Granted",
  accepted: "Granted",
  partially_accepted: "Partially Granted",
  withdrawn: "Withdrawn",
  declined: "Declined",
  pending: "Pending",
  deemed_granted: "Granted",
};

const CONSENT_STATUS_STYLES = {
  granted: { color: "#16a34a", bg: "#f0fdf4" },
  accepted: { color: "#16a34a", bg: "#f0fdf4" },
  deemed_granted: { color: "#16a34a", bg: "#f0fdf4" },
  partially_accepted: { color: "#ca8a04", bg: "#fefce8" },
  withdrawn: { color: "#C73838", bg: "#FFF0F0" },
  declined: { color: "#C73838", bg: "#FFF0F0" },
  pending: { color: "#2563eb", bg: "#eff6ff" },
};
const DEFAULT_CONSENT_STATUS_STYLE = { color: "#9ca3af", bg: "transparent" };

function StatusBadge({ status, updatedAt, direction = "vertical" }) {
  const key = (status || "").toLowerCase().replace(/[\s_]+/g, "_");
  const label = CONSENT_STATUS_LABELS[key] || status;
  const style = CONSENT_STATUS_STYLES[key] || DEFAULT_CONSENT_STATUS_STYLE;
  const isHorizontal = direction === "horizontal";
  return (
    <div className={isHorizontal ? "flex items-center gap-2" : undefined}>
      <Badge value={label} styleMap={{ [label]: style }} />
      {updatedAt && (
        <div className={`text-xs text-gray-400 ${isHorizontal ? "" : "mt-1"}`}>
          {formatDate(updatedAt)}
        </div>
      )}
    </div>
  );
}

StatusBadge.propTypes = {
  status: PropTypes.string,
  updatedAt: PropTypes.string,
  direction: PropTypes.oneOf(["vertical", "horizontal"]),
};

const STATUS_VALUES = ["Granted", "Declined", "Withdrawn"];

const toTitleCase = (s) =>
  typeof s === "string" && s.length
    ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
    : s;

function normalizePurpose(p, idx) {
  const required = Boolean(
    p.required ??
    p.is_required ??
    p.required_for_service ??
    p.mandatory ??
    false,
  );
  const rawStatus = p.purposeStatus ?? p.status;
  const rawStatusTitled = toTitleCase(rawStatus);
  const accepted = Boolean(
    p.accepted ??
    p.is_accepted ??
    p.consented ??
    (p.consent_status === "accepted" ||
      rawStatus === "Updated Preference" ||
      rawStatus === true ||
      rawStatus === undefined ||
      rawStatus === null),
  );
  const hasExplicitStatus = STATUS_VALUES.includes(rawStatusTitled);
  const status = hasExplicitStatus
    ? rawStatusTitled
    : accepted
      ? "Granted"
      : "Withdrawn";
  const statusUpdatedAt =
    p.status_updated_at ??
    p.statusUpdatedAt ??
    p.updated_at ??
    p.updatedAt ??
    "";
  return {
    id: p.id ?? p.purpose_id ?? String(idx),
    purposeId: String(p.purpose_id ?? p.id ?? idx),
    name: p.purpose ?? p.name ?? p.purpose_name ?? p.title ?? "",
    description: p.description ?? "",
    required,
    dataCategories: p.dataCategories ?? p.data_categories ?? [],
    retentionPeriod:
      p.retentionPeriod ??
      (p.retention_period_days != null
        ? `${p.retention_period_days} Days`
        : p.retention_period) ??
      "",
    status,
    statusUpdatedAt,
    hasExplicitStatus,
    selected: status === "Granted",
  };
}

function extractPurposes(responseData) {
  const data = responseData?.data;
  if (!data) return [];
  const container = data.purposes;
  const rawPurposes = Array.isArray(container)
    ? container
    : Array.isArray(container?.purposes)
      ? container.purposes
      : [];
  const consentStatus = data.consent_status || data.consentStatus || "";
  const isGranted = consentStatus.toUpperCase() === "GRANTED";
  const isDeclined = consentStatus.toLowerCase() === "declined";
  const isWithdrawn = consentStatus.toLowerCase() === "withdrawn";
  return rawPurposes
    .map((p, idx) => normalizePurpose(p, idx))
    .map((p) =>
      !p.hasExplicitStatus && isGranted && p.required
        ? { ...p, status: "Granted", selected: true }
        : p,
    )
    .map((p) =>
      !p.hasExplicitStatus && isDeclined
        ? { ...p, status: "Declined", selected: false }
        : p,
    )
    .map((p) =>
      !p.hasExplicitStatus && isWithdrawn
        ? { ...p, status: "Withdrawn", selected: false }
        : p,
    )
    .filter((p) => p.status !== "Declined");
}

const humanizeEvent = (raw) =>
  raw
    ?.toString()
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ") ?? "";

const PURPOSE_EVENT_LABELS = {
  granted: "Consent Granted",
  accepted: "Consent Granted",
  deemed_granted: "Consent Granted",
  partially_accepted: "Consent Partially Granted",
  withdrawn: "Consent Withdrawn",
  declined: "Consent Declined",
  pending: "Consent Pending",
};

// The ledger's own event_label is generic ("Consent preferences updated",
// "Notification sent") regardless of what happened to any one purpose — so the
// per-purpose history label is derived from that purpose's own status on this
// entry instead, matching the "Consent Withdrawn" / "Consent Granted" style.
const purposeEventLabel = (rawStatus, fallbackEventType) => {
  const key = (rawStatus || "").toLowerCase().replace(/[\s_]+/g, "_");
  return PURPOSE_EVENT_LABELS[key] || humanizeEvent(fallbackEventType) || "";
};

// Turn the consent-level ledger into a per-purpose history: one row per ledger
// entry that mentions this purpose, in whatever status that entry recorded it.
//
// Matched by purpose NAME, not purpose_id: the self-service "update preferences"
// call never sends a purpose id (see update_preferences.py / _merge_selected_purposes
// in nyai-dpdpa-engine), so once a user has saved preferences at least once,
// /user/purposes stops returning any id for that purpose (falls back to an array
// index) while the ledger still carries the notice snapshot's real purpose_id —
// the two would never line up. The name is the one field both sides reliably share.
function extractPurposeHistory(ledgerEntries, purposeName) {
  const target = (purposeName || "").trim().toLowerCase();
  return ledgerEntries
    .map((e) => {
      const purposes = Array.isArray(e.purposes) ? e.purposes : [];
      const match = purposes.find(
        (p) => (p.purpose || "").trim().toLowerCase() === target,
      );
      if (!match) return null;
      return {
        id: e.id,
        event: purposeEventLabel(match.status, e.event_type),
        status: match.status || "",
        timestamp: e.occurred_at ?? e.timestamp ?? e.created_at ?? "",
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
}

const historyDotColor = (status) => {
  const key = (status || "").toLowerCase().replace(/[\s_]+/g, "_");
  if (key === "withdrawn" || key === "declined") return "#EF4444";
  if (key === "granted" || key === "accepted" || key === "deemed_granted")
    return "#22C55E";
  return "#9CA3AF";
};

function PurposeRow({
  purpose,
  onToggle,
  disabled,
  history,
  historyLoading,
  historyOpen,
  onToggleHistory,
}) {
  return (
    <div className="border-b border-gray-100 last:border-0 py-4">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onToggle}
          disabled={disabled}
          aria-pressed={purpose.selected}
          className={`shrink-0 mt-0.5 w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${
            purpose.selected ? "justify-end" : "justify-start"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          style={{ background: purpose.selected ? "#2d7a7a" : "#D1D5DB" }}
        >
          <span className="w-4 h-4 rounded-full bg-white shadow" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm font-semibold text-gray-800">
              {purpose.name}
              {purpose.required && (
                <span className="text-[#C73838] ml-0.5">*</span>
              )}
            </div>
            <button
              type="button"
              onClick={onToggleHistory}
              className="shrink-0 text-xs font-medium text-[#3F707D] flex items-center gap-1"
            >
               {historyOpen ? "Hide History" : "Show History"}
              <svg
                className={`transition-transform ${historyOpen ? "rotate-180" : ""}`}
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-xs text-gray-400">Status :</span>
            <StatusBadge
              status={purpose.status}
              updatedAt={purpose.statusUpdatedAt}
              direction="horizontal"
            />
          </div>
          {purpose.dataCategories?.length > 0 && (
            <div className="flex items-center flex-wrap gap-2 mt-2">
              <span className="text-xs text-gray-400">Data Categories :</span>
              {purpose.dataCategories.map((cat) => (
                <span
                  key={cat}
                  className="inline-flex items-center gap-1 px-[9px] py-[3px] bg-[rgba(37,99,235,0.08)] text-[#2563eb] text-[12px] font-medium rounded-full"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2563eb]" />
                  {cat}
                </span>
              ))}
            </div>
          )}

          {historyOpen && (
            <div className="mt-3 pl-1 pt-3 border-t border-gray-100">
              <div className="text-xs font-semibold text-gray-400 tracking-wide mb-2">
                Timeline:
              </div>
              {historyLoading ? (
                <div className="text-xs text-gray-400 py-2 pl-3">
                  Loading history...
                </div>
              ) : history.length === 0 ? (
                <div className="text-xs text-gray-400 py-2 pl-3">
                  No history found.
                </div>
              ) : (
                <ul>
                  {history.map((h, idx) => (
                    <li key={h.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <span
                          className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                          style={{ background: historyDotColor(h.status) }}
                        />
                        {idx !== history.length - 1 && (
                          <span className="w-px flex-1 bg-gray-200" />
                        )}
                      </div>
                      <div className="pb-3">
                        <div className="text-sm font-medium text-gray-800">
                          {h.event}
                        </div>
                       
                        <div className="text-xs text-gray-400 mt-0.5">
                          {formatTimelineDate(h.timestamp)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

PurposeRow.propTypes = {
  purpose: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  history: PropTypes.array.isRequired,
  historyLoading: PropTypes.bool.isRequired,
  historyOpen: PropTypes.bool.isRequired,
  onToggleHistory: PropTypes.func.isRequired,
};

function ConsentCard({
  companyId,
  consent,
  expanded,
  onToggleExpand,
  onChanged,
  isLatestVersion = true,
}) {
  const id = consent.consent_id || consent.id;
  const name = consent.consent_name || consent.name;
  const date = consent.created_at || consent.received_on;
  const version = consent.notice_version ?? consent.version;
  const isReadOnly = !isLatestVersion;

  const [purposes, setPurposes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showMandatoryWarningModal, setShowMandatoryWarningModal] =
    useState(false);
  const [savedSelected, setSavedSelected] = useState(new Map());
  const [ledgerEntries, setLedgerEntries] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [openHistoryId, setOpenHistoryId] = useState(null);

  const grantedCount = (purposes || []).filter((p) => p.selected).length;
  const totalCount = (purposes || []).length;
  const grantedCountStatusKey =
    totalCount > 0 && grantedCount === totalCount
      ? "granted"
      : grantedCount === 0
        ? "withdrawn"
        : "partially_accepted";
  const grantedCountStyle =
    CONSENT_STATUS_STYLES[grantedCountStatusKey] || DEFAULT_CONSENT_STATUS_STYLE;

  const fetchPurposes = useCallback(() => {
    setLoading(true);
    return getPurposes(companyId, id, version)
      .then((res) => {
        const list = extractPurposes(res.data);
        setPurposes(list);
        setSavedSelected(new Map(list.map((p) => [p.purposeId, p.selected])));
      })
      .catch(() => setPurposes([]))
      .finally(() => setLoading(false));
  }, [companyId, id, version]);

  useEffect(() => {
    if (expanded && purposes === null) {
      fetchPurposes();
    }
  }, [expanded, purposes, fetchPurposes]);

  const handleToggleHistory = (purposeId) => {
    const next = openHistoryId === purposeId ? null : purposeId;
    setOpenHistoryId(next);
    if (next && ledgerEntries === null && !ledgerLoading) {
      setLedgerLoading(true);
      getConsentLedger(id, companyId, version)
        .then((res) => {
          const raw =
            res?.data?.data?.items ?? res?.data?.data ?? res?.data ?? [];
          setLedgerEntries(Array.isArray(raw) ? raw : []);
        })
        .catch(() => setLedgerEntries([]))
        .finally(() => setLedgerLoading(false));
    }
  };

  const togglePurpose = (purposeId) =>
    setPurposes((prev) =>
      prev.map((p) =>
        p.purposeId === purposeId ? { ...p, selected: !p.selected } : p,
      ),
    );

  const hasPendingChanges = (purposes || []).some(
    (p) => p.selected !== savedSelected.get(p.purposeId),
  );
  const hasDeselectedMandatory = (purposes || []).some(
    (p) => p.required && !p.selected,
  );
  const hasWithdrawn = (purposes || []).some((p) => p.status === "Withdrawn");
  const isSinglePurpose = totalCount === 1;
  const withdrawLabel = isSinglePurpose ? "Withdraw" : "Withdraw All";

  const buildSelectedPurposes = (list, statusUpdatedAt) =>
    list.map((p) => {
      const changed = p.selected !== savedSelected.get(p.purposeId);
      return {
        purpose: p.name,
        required: p.required,
        description: p.description,
        dataCategories: p.dataCategories,
        retentionPeriod: p.retentionPeriod,
        status: p.selected ? "GRANTED" : "WITHDRAWN",
        status_updated_at: changed
          ? statusUpdatedAt
          : p.statusUpdatedAt || statusUpdatedAt,
      };
    });

  const handleSave = async () => {
    if (!hasPendingChanges) return;
    setShowMandatoryWarningModal(false);
    setSaving(true);
    try {
      const statusUpdatedAt = new Date().toISOString();
      const selectedPurposes = buildSelectedPurposes(purposes, statusUpdatedAt);
      const allGranted = purposes.every((p) => p.selected);
      const someGranted = purposes.some((p) => p.selected);
      const consentStatus = allGranted
        ? "GRANTED"
        : someGranted
          ? "PARTIALLY_ACCEPTED"
          : "WITHDRAWN";
      await updateConsentPreferences(
        companyId,
        id,
        selectedPurposes,
        consentStatus,
        version,
      );
      setLedgerEntries(null);
      await fetchPurposes();
      await onChanged();
      toast.success("Preferences updated successfully!");
    } catch {
      toast.error("Failed to update preferences. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClick = () => {
    if (!hasPendingChanges) return;
    if (hasDeselectedMandatory) {
      setShowMandatoryWarningModal(true);
      return;
    }
    handleSave();
  };

  const handleWithdrawAll = async () => {
    setShowWithdrawModal(false);
    setSaving(true);
    try {
      const statusUpdatedAt = new Date().toISOString();
      const selectedPurposes = purposes.map((p) => ({
        purpose: p.name,
        required: p.required,
        description: p.description,
        dataCategories: p.dataCategories,
        retentionPeriod: p.retentionPeriod,
        status: "WITHDRAWN",
        status_updated_at:
          p.status !== "Withdrawn"
            ? statusUpdatedAt
            : p.statusUpdatedAt || statusUpdatedAt,
      }));
      await withdrawAllConsent(companyId, id, selectedPurposes, version);
      setLedgerEntries(null);
      await fetchPurposes();
      await onChanged();
      toast.success("Consent withdrawn successfully!");
    } catch {
      toast.error("Failed to withdraw consent. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Accordion
        className="border-[#DCE3E6]"
        open={expanded}
        onToggle={onToggleExpand}
        header={
          <div className="flex-1 flex items-center justify-between gap-3 min-w-0">
            <div>
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-gray-800">{name}</div>
                {version != null && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    v{version}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Received On :{" "}
                <span className="text-gray-600 font-medium">
                  {formatDate(date)}
                </span>
              </div>
            </div>
            {expanded && totalCount > 0 ? (
              <Badge
                value={`${grantedCount} of ${totalCount} Granted`}
                styleMap={{
                  [`${grantedCount} of ${totalCount} Granted`]: grantedCountStyle,
                }}
              />
            ) : (
              <StatusBadge status={consent.status} />
            )}
          </div>
        }
      >
        <div className="text-xs font-semibold text-gray-400 tracking-wide pb-1 border-b border-gray-100">
          PURPOSE(S)
        </div>
        {loading || purposes === null ? (
          <div className="py-6 text-center text-gray-400 text-sm">
            Loading...
          </div>
        ) : purposes.length === 0 ? (
          <div className="py-6 text-center text-gray-400 text-sm">
            No purposes found.
          </div>
        ) : (
          <>
            {purposes.map((purpose) => (
              <PurposeRow
                key={purpose.purposeId}
                purpose={purpose}
                onToggle={() => togglePurpose(purpose.purposeId)}
                disabled={isReadOnly}
                history={
                  ledgerEntries
                    ? extractPurposeHistory(ledgerEntries, purpose.name)
                    : []
                }
                historyLoading={
                  ledgerLoading && openHistoryId === purpose.purposeId
                }
                historyOpen={openHistoryId === purpose.purposeId}
                onToggleHistory={() =>
                  handleToggleHistory(purpose.purposeId)
                }
              />
            ))}

            <div className="flex justify-end gap-3 pt-4">
              {!isSinglePurpose && !hasWithdrawn && (
                <Button
                  variant="outline"
                  onClick={() => setShowWithdrawModal(true)}
                  disabled={saving || isReadOnly}
                  className="!border-[#C73838] !text-[#C73838] hover:!bg-red-50"
                >
                  {withdrawLabel}
                </Button>
              )}
              <Button
                onClick={handleSaveClick}
                disabled={saving || !hasPendingChanges || isReadOnly}
                style={{ background: "#2d7a7a" }}
              >
                {saving ? "Saving..." : "Save Preferences"}
              </Button>
            </div>
          </>
        )}
      </Accordion>

      {showMandatoryWarningModal && (
        <Modal
          onClose={() => setShowMandatoryWarningModal(false)}
          showClose={false}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setShowMandatoryWarningModal(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                style={{ background: "#2d7a7a" }}
              >
                {saving ? "Saving..." : "Proceed"}
              </Button>
            </>
          }
        >
          <Typography variant="h2" className="mb-2">
            Deselected a mandatory purpose
          </Typography>
          <Typography variant="body" color="muted">
            You have deselected a mandatory purpose. Continuing may prevent
            you from accessing certain services. Do you want to proceed?
          </Typography>
        </Modal>
      )}

      {showWithdrawModal && (
        <Modal
          onClose={() => setShowWithdrawModal(false)}
          showClose={false}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setShowWithdrawModal(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleWithdrawAll}
                disabled={saving}
              >
                {saving ? "Withdrawing..." : withdrawLabel}
              </Button>
            </>
          }
        >
          <Typography variant="h2" className="mb-2">
            {isSinglePurpose ? "Withdraw consent?" : "Withdraw all consents?"}
          </Typography>
          <Typography variant="body" color="muted">
            {isSinglePurpose
              ? "This will withdraw consent for this purpose under this notice. You can grant consent again later."
              : "This will withdraw consent for all purposes under this notice. You can grant consent again later."}
          </Typography>
        </Modal>
      )}
    </>
  );
}

ConsentCard.propTypes = {
  companyId: PropTypes.string.isRequired,
  consent: PropTypes.object.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  isLatestVersion: PropTypes.bool,
};

export default function ConsentList() {
  const { companyId } = useParams();
  const location = useLocation();
  const companyName = location.state?.companyName || "Company";

  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState("current");
  const pageSize = 10;

  const fetchConsents = useCallback(() => {
    setLoading(true);
    // The API does not reliably scope /user/consents results to the requested
    // tenant_id (it can return consents belonging to other tenants too), so
    // page through the full matching set ourselves and filter it.
    const fetchAllPages = (pageNum, accumulated) =>
      getConsentsByTenant(companyId, {
        page: pageNum,
        page_size: pageSize,
      }).then((res) => {
        const items = res.data.data?.items || [];
        const grandTotal = res.data.data?.total || 0;
        const combined = accumulated.concat(items);
        if (combined.length >= grandTotal || items.length === 0) {
          return combined;
        }
        return fetchAllPages(pageNum + 1, combined);
      });

    return fetchAllPages(1, [])
      .then((allItems) => {
        // Declined consents are excluded from this list entirely.
        const tenantItems = allItems
          .filter((c) => (c.status || "").toUpperCase() !== "DECLINED")
          .filter((c) => !c.tenant_id || c.tenant_id === companyId);
        setConsents(tenantItems);
      })
      .catch(() => setConsents([]))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => {
    fetchConsents();
  }, [fetchConsents]);

  // When the same notice has been re-issued (multiple entries sharing the
  // same consent_name with different notice_version), only the highest
  // version per name is "Currently Consented" — the rest are "Previously
  // Consented" and shown read-only.
  const { currentVersions, previousVersions } = useMemo(() => {
    const order = [];
    const map = new Map();
    consents.forEach((c) => {
      const name = c.consent_name || c.name;
      if (!map.has(name)) {
        map.set(name, []);
        order.push(name);
      }
      map.get(name).push(c);
    });
    const current = [];
    const previous = [];
    order.forEach((name) => {
      const [latest, ...rest] = map
        .get(name)
        .slice()
        .sort(
          (a, b) =>
            Number(b.notice_version ?? b.version ?? 0) -
            Number(a.notice_version ?? a.version ?? 0),
        );
      current.push(latest);
      previous.push(...rest);
    });
    return { currentVersions: current, previousVersions: previous };
  }, [consents]);

  const visibleConsents =
    activeTab === "current" ? currentVersions : previousVersions;

  return (
    <div className="p-4 md:p-6 pb-4 md:pb-6">
      <Tabs
        className="mb-4"
        tabs={[
          { id: "current", label: "Current Consents" },
          { id: "previous", label: "Past Consents" },
        ]}
        activeId={activeTab}
        onChange={setActiveTab}
      />

      <div className="flex flex-col gap-3">
        {loading ? (
          <div className="py-10 text-center text-gray-400 text-sm">
            Loading...
          </div>
        ) : visibleConsents.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">
            No consents found.
          </div>
        ) : (
          visibleConsents.map((consent, idx) => {
            const id =
              consent.consent_id ||
              consent.id ||
              `${consent.consent_name || consent.name}-v${consent.notice_version ?? consent.version}`;
            // consent_id is shared across every notice version of the same consent (only
            // notice_version differs), so keying on `id` alone collides whenever more than
            // one version of the same consent is in play — switching tabs (or listing
            // multiple past versions of one consent) would make React reuse the same
            // ConsentCard instance, and its cached `purposes` state, across two different
            // versions' data. The key must include the version to force a fresh mount.
            const version = consent.notice_version ?? consent.version;
            const cardKey = `${id || idx}-v${version ?? idx}`;
            return (
              <ConsentCard
                key={cardKey}
                companyId={companyId}
                consent={consent}
                expanded={expandedId === cardKey}
                onToggleExpand={() =>
                  setExpandedId((prev) =>
                    prev === cardKey ? null : cardKey,
                  )
                }
                onChanged={fetchConsents}
                isLatestVersion={activeTab === "current"}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
